import React, { useState, useEffect } from 'react';
import { HedisMetricsTypeEnum } from '../Enum/HedisMetricsTypeEnum';
import { TimePeriodFilterEnum } from '../Enum/TimePeriodFilterEnum';

const HedisMetricsContext = React.createContext<any>(null);
const LOCAL_STORAGE_KEY = 'hedisMetricsContext';
const allHealthPlan = 'All';

const getInitialState = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
        return saved || {};
    } catch {
        return {};
    }
};

// Default values
const defaultState = {
    hedisType: HedisMetricsTypeEnum.BP,
    healthPlans: [allHealthPlan],
    population: 0,
    team: '',
    filter: TimePeriodFilterEnum.Last6Months,
    fromDate: null,
    toDate: null,
    loadedTeam: false,
    includePastOpenStatus: false,
    showInActiveTeams: false,
    hasElevatedFilterAccess: false,
};

export const HedisMetricsProvider = ({ children }: { children: React.ReactNode }) => {
    const initial = getInitialState();

    const [selectedTeam, setSelectedTeam] = useState(initial.selectedTeam || '');
    const [showInActiveTeams, setShowInActiveTeams] = useState(initial.showInActiveTeams || false);

    const [hedisType, setHedisType] = useState(initial.hedisType || HedisMetricsTypeEnum.BP);
    const [healthPlans, setHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.healthPlans)) {
            return initial.healthPlans.includes(allHealthPlan) ? [] : initial.healthPlans;
        }

        if (initial.healthPlans && initial.healthPlans !== allHealthPlan) {
            return [initial.healthPlans];
        }

        return [];
    });
    const [population, setPopulation] = useState(initial?.population ?? 0);
    const [team, setTeam] = useState(initial.team || '');
    const [filter, setFilter] = useState(initial.filter || TimePeriodFilterEnum.Last6Months);
    const [fDate, setFDate] = useState(initial.fDate || null);
    const [tDate, setTDate] = useState(initial.tDate || null);
    const [loadedTeam, setLoadedTeam] = useState(initial.loadedTeam || false);
    const [includePastOpenStatus, setIncludePastOpenStatus] = useState(initial.includePastOpenStatus || false);
    const [showInActiveTeam, setShowInActiveTeam] = useState(initial.showInActiveTeam || false);
    const [hasElevatedFilterAccess, setHasElevatedFilterAccess] = useState(initial.hasElevatedFilterAccess || false);

    const [adultHealthHealthPlans, setAdultHealthHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.adultHealthHealthPlans)) {
            return initial.adultHealthHealthPlans.includes(allHealthPlan)
                ? []
                : initial.adultHealthHealthPlans;
        }

        if (
            initial.adultHealthHealthPlans &&
            initial.adultHealthHealthPlans !== allHealthPlan
        ) {
            return [initial.adultHealthHealthPlans];
        }

        return [];
    });
    const [adultHealthTeam, setAdultHealthTeam] = useState(initial.adultHealthTeam || '');
    const [adultHealthDateFilter, setAdultHealthDateFilter] = useState(initial.adultHealthDateFilter || TimePeriodFilterEnum.Last6Months);
    const [adultHealthFromDate, setAdultHealthFromDate] = useState(initial.adultHealthFromDate || null);
    const [adultHealthToDate, setAdultHealthToDate] = useState(initial.adultHealthToDate || null);
    const [adultHealthLoadedTeam, setAdultHealthLoadedTeam] = useState(initial.adultHealthLoadedTeam || false);
    const [adultHealthIncludePastOpenStatus, setAdultHealthIncludePastOpenStatus] = useState(initial.adultHealthIncludePastOpenStatus || false);
    const [adultHealthShowInActiveTeam, setAdultHealthShowInActiveTeam] = useState(initial.adultHealthShowInActiveTeam || false);

    const [bmiHealthPlans, setBmiHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.bmiHealthPlans)) {
            return initial.bmiHealthPlans.includes(allHealthPlan)
                ? []
                : initial.bmiHealthPlans;
        }

        if (initial.bmiHealthPlans && initial.bmiHealthPlans !== allHealthPlan) {
            return [initial.bmiHealthPlans];
        }

        return [];
    });
    const [bmiTeam, setBmiTeam] = useState(initial.bmiTeam || '');
    const [bmiDateFilter, setBmiDateFilter] = useState(initial.bmiDateFilter || TimePeriodFilterEnum.Last6Months);
    const [bmiFromDate, setBmiFromDate] = useState(initial.bmiFromDate || null);
    const [bmiToDate, setBmiToDate] = useState(initial.bmiToDate || null);
    const [bmiLoadedTeam, setBmiLoadedTeam] = useState(initial.bmiLoadedTeam || false);
    const [bmiIncludePastOpenStatus, setBmiIncludePastOpenStatus] = useState(initial.bmiIncludePastOpenStatus || false);
    const [bmiShowInActiveTeam, setBmiShowInActiveTeam] = useState(initial.bmiShowInActiveTeam || false);

    const [bpHealthPlans, setBpHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.bpHealthPlans)) {
            return initial.bpHealthPlans.includes(allHealthPlan)
                ? []
                : initial.bpHealthPlans;
        }

        if (initial.bpHealthPlans && initial.bpHealthPlans !== allHealthPlan) {
            return [initial.bpHealthPlans];
        }

        return [];
    });
    const [bpPopulation, setBpPopulation] = useState(initial?.bpPopulation ?? 0);
    const [bpTeam, setBpTeam] = useState(initial.bpTeam || '');
    const [bpDateFilter, setBpDateFilter] = useState(initial.bpDateFilter || TimePeriodFilterEnum.Last6Months);
    const [bpFromDate, setBpFromDate] = useState(initial.bpFromDate || null);
    const [bpToDate, setBpToDate] = useState(initial.bpToDate || null);
    const [bpLoadedTeam, setBpLoadedTeam] = useState(initial.bpLoadedTeam || false);
    const [bpIncludePastOpenStatus, setBpIncludePastOpenStatus] = useState(initial.bpIncludePastOpenStatus || false);
    const [bpShowInActiveTeam, setBpShowInActiveTeam] = useState(initial.bpShowInActiveTeam || false);

    const [
        breastCancerScreeningHealthPlans,
        setBreastCancerScreeningHealthPlans
    ] = useState<string[]>(() => {
        if (Array.isArray(initial.breastCancerScreeningHealthPlans)) {
            return initial.breastCancerScreeningHealthPlans.includes(allHealthPlan)
                ? []
                : initial.breastCancerScreeningHealthPlans;
        }

        if (
            initial.breastCancerScreeningHealthPlans &&
            initial.breastCancerScreeningHealthPlans !== allHealthPlan
        ) {
            return [initial.breastCancerScreeningHealthPlans];
        }

        return [];
    });
    const [breastCancerScreeningTeam, setBreastCancerScreeningTeam] = useState(initial.breastCancerScreeningTeam || '');
    const [breastCancerScreeningDateFilter, setBreastCancerScreeningDateFilter] = useState(
        initial.breastCancerScreeningDateFilter || TimePeriodFilterEnum.Last6Months
    );
    const [breastCancerScreeningFromDate, setBreastCancerScreeningFromDate] = useState(initial.breastCancerScreeningFromDate || null);
    const [breastCancerScreeningToDate, setBreastCancerScreeningToDate] = useState(initial.breastCancerScreeningToDate || null);
    const [breastCancerScreeningLoadedTeam, setBreastCancerScreeningLoadedTeam] = useState(initial.breastCancerScreeningLoadedTeam || false);
    const [breastCancerScreeningIncludePastOpenStatus, setBreastCancerScreeningIncludePastOpenStatus] = useState(
        initial.breastCancerScreeningIncludePastOpenStatus || false
    );
    const [breastCancerScreeningShowInActiveTeam, setBreastCancerScreeningShowInActiveTeam] = useState(initial.breastCancerScreeningShowInActiveTeam || false);

    const [depressionFollowUpHealthPlans, setDepressionFollowUpHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.depressionFollowUpHealthPlans)) {
            return initial.depressionFollowUpHealthPlans.includes(allHealthPlan)
                ? []
                : initial.depressionFollowUpHealthPlans;
        }

        if (initial.depressionFollowUpHealthPlans && initial.depressionFollowUpHealthPlans !== allHealthPlan) {
            return [initial.depressionFollowUpHealthPlans];
        }

        return [];
    });
    const [depressionFollowUpTeam, setDepressionFollowUpTeam] = useState(initial.depressionFollowUpTeam || '');
    const [depressionFollowUpDateFilter, setDepressionFollowUpDateFilter] = useState(initial.depressionFollowUpDateFilter || TimePeriodFilterEnum.Last6Months);
    const [depressionFollowUpFromDate, setDepressionFollowUpFromDate] = useState(initial.depressionFollowUpFromDate || null);
    const [depressionFollowUpToDate, setDepressionFollowUpToDate] = useState(initial.depressionFollowUpToDate || null);
    const [depressionFollowUpLoadedTeam, setDepressionFollowUpLoadedTeam] = useState(initial.depressionFollowUpLoadedTeam || false);
    const [depressionFollowUpIncludePastOpenStatus, setDepressionFollowUpIncludePastOpenStatus] = useState(
        initial.depressionFollowUpIncludePastOpenStatus || false
    );
    const [depressionFollowUpShowInActiveTeam, setDepressionFollowUpShowInActiveTeam] = useState(initial.depressionFollowUpShowInActiveTeam || false);

    const [depressionScreeningHealthPlans, setDepressionScreeningHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.depressionScreeningHealthPlans)) {
            return initial.depressionScreeningHealthPlans.includes(allHealthPlan)
                ? []
                : initial.depressionScreeningHealthPlans;
        }

        if (initial.depressionScreeningHealthPlans && initial.depressionScreeningHealthPlans !== allHealthPlan) {
            return [initial.depressionScreeningHealthPlans];
        }

        return [];
    });
    const [depressionScreeningTeam, setDepressionScreeningTeam] = useState(initial.depressionScreeningTeam || '');
    const [depressionScreeningDateFilter, setDepressionScreeningDateFilter] = useState(
        initial.depressionScreeningDateFilter || TimePeriodFilterEnum.Last6Months
    );
    const [depressionScreeningFromDate, setDepressionScreeningFromDate] = useState(initial.depressionScreeningFromDate || null);
    const [depressionScreeningToDate, setDepressionScreeningToDate] = useState(initial.depressionScreeningToDate || null);
    const [depressionScreeningLoadedTeam, setDepressionScreeningLoadedTeam] = useState(initial.depressionScreeningLoadedTeam || false);
    const [depressionScreeningIncludePastOpenStatus, setDepressionScreeningIncludePastOpenStatus] = useState(
        initial.depressionScreeningIncludePastOpenStatus || false
    );
    const [depressionScreeningShowInActiveTeam, setDepressionScreeningShowInActiveTeam] = useState(initial.depressionScreeningShowInActiveTeam || false);

    const [diabeticEyeExamHealthPlans, setDiabeticEyeExamHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.diabeticEyeExamHealthPlans)) {
            return initial.diabeticEyeExamHealthPlans.includes(allHealthPlan)
                ? []
                : initial.diabeticEyeExamHealthPlans;
        }

        if (initial.diabeticEyeExamHealthPlans && initial.diabeticEyeExamHealthPlans !== allHealthPlan) {
            return [initial.diabeticEyeExamHealthPlans];
        }

        return [];
    });
    const [diabeticEyeExamTeam, setDiabeticEyeExamTeam] = useState(initial.diabeticEyeExamTeam || '');
    const [diabeticEyeExamDateFilter, setDiabeticEyeExamDateFilter] = useState(initial.diabeticEyeExamDateFilter || TimePeriodFilterEnum.Last6Months);
    const [diabeticEyeExamFromDate, setDiabeticEyeExamFromDate] = useState(initial.diabeticEyeExamFromDate || null);
    const [diabeticEyeExamToDate, setDiabeticEyeExamToDate] = useState(initial.diabeticEyeExamToDate || null);
    const [diabeticEyeExamLoadedTeam, setDiabeticEyeExamLoadedTeam] = useState(initial.diabeticEyeExamLoadedTeam || false);
    const [diabeticEyeExamIncludePastOpenStatus, setDiabeticEyeExamIncludePastOpenStatus] = useState(initial.diabeticEyeExamIncludePastOpenStatus || false);
    const [diabeticEyeExamShowInActiveTeam, setDiabeticEyeExamShowInActiveTeam] = useState(initial.diabeticEyeExamShowInActiveTeam || false);

    const [hgba1cHealthPlans, setHgba1cHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.hgba1cHealthPlans)) {
            return initial.hgba1cHealthPlans.includes(allHealthPlan)
                ? []
                : initial.hgba1cHealthPlans;
        }

        if (initial.hgba1cHealthPlans && initial.hgba1cHealthPlans !== allHealthPlan) {
            return [initial.hgba1cHealthPlans];
        }

        return [];
    });
    const [hgba1cPopulation, setHgba1cPopulation] = useState(initial?.hgba1cPopulation ?? 0);
    const [hgba1cTeam, setHgba1cTeam] = useState(initial.hgba1cTeam || '');
    const [hgba1cDateFilter, setHgba1cDateFilter] = useState(initial.hgba1cDateFilter || TimePeriodFilterEnum.Last6Months);
    const [hgba1cFromDate, setHgba1cFromDate] = useState(initial.hgba1cFromDate || null);
    const [hgba1cToDate, setHgba1cToDate] = useState(initial.hgba1cToDate || null);
    const [hgba1cLoadedTeam, setHgba1cLoadedTeam] = useState(initial.hgba1cLoadedTeam || false);
    const [hgba1cIncludePastOpenStatus, setHgba1cIncludePastOpenStatus] = useState(initial.hgba1cIncludePastOpenStatus || false);
    const [hgba1cShowInActiveTeam, setHgba1cShowInActiveTeam] = useState(initial.hgba1cShowInActiveTeam || false);

    const [hgbA1cForBhDxHealthPlans, setHgbA1cForBhDxHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.hgbA1cForBhDxHealthPlans)) {
            return initial.hgbA1cForBhDxHealthPlans.includes(allHealthPlan)
                ? []
                : initial.hgbA1cForBhDxHealthPlans;
        }

        if (initial.hgbA1cForBhDxHealthPlans && initial.hgbA1cForBhDxHealthPlans !== allHealthPlan) {
            return [initial.hgbA1cForBhDxHealthPlans];
        }

        return [];
    });
    const [hgbA1cForBhDxTeam, setHgbA1cForBhDxTeam] = useState(initial.hgbA1cForBhDxTeam || '');
    const [hgbA1cForBhDxDateFilter, setHgbA1cForBhDxDateFilter] = useState(initial.hgbA1cForBhDxDateFilter || TimePeriodFilterEnum.Last6Months);
    const [hgbA1cForBhDxFromDate, setHgbA1cForBhDxFromDate] = useState(initial.hgbA1cForBhDxFromDate || null);
    const [hgbA1cForBhDxToDate, setHgbA1cForBhDxToDate] = useState(initial.hgbA1cForBhDxToDate || null);
    const [hgbA1cForBhDxLoadedTeam, setHgbA1cForBhDxLoadedTeam] = useState(initial.hgbA1cForBhDxLoadedTeam || false);
    const [hgbA1cForBhDxIncludePastOpenStatus, setHgbA1cForBhDxIncludePastOpenStatus] = useState(initial.hgbA1cForBhDxIncludePastOpenStatus || false);
    const [hgbA1cForBhDxShowInActiveTeam, setHgbA1cForBhDxShowInActiveTeam] = useState(initial.hgbA1cForBhDxShowInActiveTeam || false);

    const [lipidForBhDxHealthPlans, setLipidForBhDxHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.lipidForBhDxHealthPlans)) {
            return initial.lipidForBhDxHealthPlans.includes(allHealthPlan)
                ? []
                : initial.lipidForBhDxHealthPlans;
        }

        if (initial.lipidForBhDxHealthPlans && initial.lipidForBhDxHealthPlans !== allHealthPlan) {
            return [initial.lipidForBhDxHealthPlans];
        }

        return [];
    });
    const [lipidForBhDxTeam, setLipidForBhDxTeam] = useState(initial.lipidForBhDxTeam || '');
    const [lipidForBhDxDateFilter, setLipidForBhDxDateFilter] = useState(initial.lipidForBhDxDateFilter || TimePeriodFilterEnum.Last6Months);
    const [lipidForBhDxFromDate, setLipidForBhDxFromDate] = useState(initial.lipidForBhDxFromDate || null);
    const [lipidForBhDxToDate, setLipidForBhDxToDate] = useState(initial.lipidForBhDxToDate || null);
    const [lipidForBhDxLoadedTeam, setLipidForBhDxLoadedTeam] = useState(initial.lipidForBhDxLoadedTeam || false);
    const [lipidForBhDxIncludePastOpenStatus, setLipidForBhDxIncludePastOpenStatus] = useState(initial.lipidForBhDxIncludePastOpenStatus || false);
    const [lipidForBhDxShowInActiveTeam, setLipidForBhDxShowInActiveTeam] = useState(initial.lipidForBhDxShowInActiveTeam || false);

    const [medicationReconciliationHealthPlans, setMedicationReconciliationHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.medicationReconciliationHealthPlans)) {
            return initial.medicationReconciliationHealthPlans.includes(allHealthPlan)
                ? []
                : initial.medicationReconciliationHealthPlans;
        }

        if (initial.medicationReconciliationHealthPlans && initial.medicationReconciliationHealthPlans !== allHealthPlan) {
            return [initial.medicationReconciliationHealthPlans];
        }

        return [];
    });
    const [medicationReconciliationTeam, setMedicationReconciliationTeam] = useState(initial.medicationReconciliationTeam || '');
    const [medicationReconciliationDateFilter, setMedicationReconciliationDateFilter] = useState(
        initial.medicationReconciliationDateFilter || TimePeriodFilterEnum.Last6Months
    );
    const [medicationReconciliationFromDate, setMedicationReconciliationFromDate] = useState(initial.medicationReconciliationFromDate || null);
    const [medicationReconciliationToDate, setMedicationReconciliationToDate] = useState(initial.medicationReconciliationToDate || null);
    const [medicationReconciliationLoadedTeam, setMedicationReconciliationLoadedTeam] = useState(initial.medicationReconciliationLoadedTeam || false);
    const [medicationReconciliationIncludePastOpenStatus, setMedicationReconciliationIncludePastOpenStatus] = useState(
        initial.medicationReconciliationIncludePastOpenStatus || false
    );
    const [medicationReconciliationShowInActiveTeam, setMedicationReconciliationShowInActiveTeam] = useState(
        initial.medicationReconciliationShowInActiveTeam || false
    );

    const [cervicalCancerScreeningHealthPlans, setCervicalCancerScreeningHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.cervicalCancerScreeningHealthPlans)) {
            return initial.cervicalCancerScreeningHealthPlans.includes(allHealthPlan)
                ? []
                : initial.cervicalCancerScreeningHealthPlans;
        }

        if (initial.cervicalCancerScreeningHealthPlans && initial.cervicalCancerScreeningHealthPlans !== allHealthPlan) {
            return [initial.cervicalCancerScreeningHealthPlans];
        }

        return [];
    });
    const [cervicalCancerScreeningTeam, setCervicalCancerScreeningTeam] = useState(initial.cervicalCancerScreeningTeam || '');
    const [cervicalCancerScreeningDateFilter, setCervicalCancerScreeningDateFilter] = useState(
        initial.cervicalCancerScreeningDateFilter || TimePeriodFilterEnum.Last6Months
    );
    const [cervicalCancerScreeningFromDate, setCervicalCancerScreeningFromDate] = useState(initial.cervicalCancerScreeningFromDate || null);
    const [cervicalCancerScreeningToDate, setCervicalCancerScreeningToDate] = useState(initial.cervicalCancerScreeningToDate || null);
    const [cervicalCancerScreeningLoadedTeam, setCervicalCancerScreeningLoadedTeam] = useState(initial.cervicalCancerScreeningLoadedTeam || false);
    const [cervicalCancerScreeningIncludePastOpenStatus, setCervicalCancerScreeningIncludePastOpenStatus] = useState(
        initial.cervicalCancerScreeningIncludePastOpenStatus || false
    );
    const [cervicalCancerScreeningShowInActiveTeam, setCervicalCancerScreeningShowInActiveTeam] = useState(
        initial.cervicalCancerScreeningShowInActiveTeam || false
    );

    const [colorectalCancerScreeningHealthPlans, setColorectalCancerScreeningHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.colorectalCancerScreeningHealthPlans)) {
            return initial.colorectalCancerScreeningHealthPlans.includes(allHealthPlan)
                ? []
                : initial.colorectalCancerScreeningHealthPlans;
        }

        if (initial.colorectalCancerScreeningHealthPlans && initial.colorectalCancerScreeningHealthPlans !== allHealthPlan) {
            return [initial.colorectalCancerScreeningHealthPlans];
        }

        return [];
    });
    const [colorectalCancerScreeningTeam, setColorectalCancerScreeningTeam] = useState(initial.colorectalCancerScreeningTeam || '');
    const [colorectalCancerScreeningDateFilter, setColorectalCancerScreeningDateFilter] = useState(
        initial.colorectalCancerScreeningDateFilter || TimePeriodFilterEnum.Last6Months
    );
    const [colorectalCancerScreeningFromDate, setColorectalCancerScreeningFromDate] = useState(initial.colorectalCancerScreeningFromDate || null);
    const [colorectalCancerScreeningToDate, setColorectalCancerScreeningToDate] = useState(initial.colorectalCancerScreeningToDate || null);
    const [colorectalCancerScreeningLoadedTeam, setColorectalCancerScreeningLoadedTeam] = useState(initial.colorectalCancerScreeningLoadedTeam || false);
    const [colorectalCancerScreeningIncludePastOpenStatus, setColorectalCancerScreeningIncludePastOpenStatus] = useState(
        initial.colorectalCancerScreeningIncludePastOpenStatus || false
    );
    const [colorectalCancerScreeningShowInActiveTeam, setColorectalCancerScreeningShowInActiveTeam] = useState(
        initial.colorectalCancerScreeningShowInActiveTeam || false
    );

    const [bhDiagnosticHealthPlans, setBhDiagnosticHealthPlans] = useState<string[]>(() => {
        if (Array.isArray(initial.bhDiagnosticHealthPlans)) {
            return initial.bhDiagnosticHealthPlans.includes(allHealthPlan)
                ? []
                : initial.bhDiagnosticHealthPlans;
        }

        if (initial.bhDiagnosticHealthPlans && initial.bhDiagnosticHealthPlans !== allHealthPlan) {
            return [initial.bhDiagnosticHealthPlans];
        }

        return [];
    });
    const [bhDiagnosticTeam, setBhDiagnosticTeam] = useState(initial.bhDiagnosticTeam || '');
    const [bhDiagnosticDateFilter, setBhDiagnosticDateFilter] = useState(initial.bhDiagnosticDateFilter || TimePeriodFilterEnum.Last6Months);
    const [bhDiagnosticFromDate, setBhDiagnosticFromDate] = useState(initial.bhDiagnosticFromDate || null);
    const [bhDiagnosticToDate, setBhDiagnosticToDate] = useState(initial.bhDiagnosticToDate || null);
    const [bhDiagnosticLoadedTeam, setBhDiagnosticLoadedTeam] = useState(initial.bhDiagnosticLoadedTeam || false);
    const [bhDiagnosticIncludePastOpenStatus, setBhDiagnosticIncludePastOpenStatus] = useState(initial.bhDiagnosticIncludePastOpenStatus || false);
    const [bhDiagnosticShowInActiveTeam, setBhDiagnosticShowInActiveTeam] = useState(initial.bhDiagnosticShowInActiveTeam || false);

    const resetHedisMetricContext = () => {
        // Reset shared HEDIS state
        setSelectedTeam(defaultState.team);
        setShowInActiveTeams(defaultState.showInActiveTeams);

        setHedisType(defaultState.hedisType);
        setHealthPlans(defaultState.healthPlans);
        setPopulation(defaultState.population);
        setTeam(defaultState.team);
        setFilter(defaultState.filter);
        setFDate(defaultState.fromDate);
        setTDate(defaultState.toDate);
        setLoadedTeam(defaultState.loadedTeam);
        setIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setShowInActiveTeam(defaultState.showInActiveTeams);
        setHasElevatedFilterAccess(defaultState.hasElevatedFilterAccess);

        // Adult Health
        setAdultHealthHealthPlans(defaultState.healthPlans);
        setAdultHealthTeam(defaultState.team);
        setAdultHealthDateFilter(defaultState.filter);
        setAdultHealthFromDate(defaultState.fromDate);
        setAdultHealthToDate(defaultState.toDate);
        setAdultHealthLoadedTeam(defaultState.loadedTeam);
        setAdultHealthIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setAdultHealthShowInActiveTeam(defaultState.showInActiveTeams);

        // BMI
        setBmiHealthPlans(defaultState.healthPlans);
        setBmiTeam(defaultState.team);
        setBmiDateFilter(defaultState.filter);
        setBmiFromDate(defaultState.fromDate);
        setBmiToDate(defaultState.toDate);
        setBmiLoadedTeam(defaultState.loadedTeam);
        setBmiIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setBmiShowInActiveTeam(defaultState.showInActiveTeams);

        // BP
        setBpHealthPlans(defaultState.healthPlans);
        setBpPopulation(defaultState.population);
        setBpTeam(defaultState.team);
        setBpDateFilter(defaultState.filter);
        setBpFromDate(defaultState.fromDate);
        setBpToDate(defaultState.toDate);
        setBpLoadedTeam(defaultState.loadedTeam);
        setBpIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setBpShowInActiveTeam(defaultState.showInActiveTeams);

        // Breast Cancer Screening
        setBreastCancerScreeningHealthPlans(defaultState.healthPlans);
        setBreastCancerScreeningTeam(defaultState.team);
        setBreastCancerScreeningDateFilter(defaultState.filter);
        setBreastCancerScreeningFromDate(defaultState.fromDate);
        setBreastCancerScreeningToDate(defaultState.toDate);
        setBreastCancerScreeningLoadedTeam(defaultState.loadedTeam);
        setBreastCancerScreeningIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setBreastCancerScreeningShowInActiveTeam(defaultState.showInActiveTeams);

        // Depression Follow-Up
        setDepressionFollowUpHealthPlans(defaultState.healthPlans);
        setDepressionFollowUpTeam(defaultState.team);
        setDepressionFollowUpDateFilter(defaultState.filter);
        setDepressionFollowUpFromDate(defaultState.fromDate);
        setDepressionFollowUpToDate(defaultState.toDate);
        setDepressionFollowUpLoadedTeam(defaultState.loadedTeam);
        setDepressionFollowUpIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setDepressionFollowUpShowInActiveTeam(defaultState.showInActiveTeams);

        // Depression Screening
        setDepressionScreeningHealthPlans(defaultState.healthPlans);
        setDepressionScreeningTeam(defaultState.team);
        setDepressionScreeningDateFilter(defaultState.filter);
        setDepressionScreeningFromDate(defaultState.fromDate);
        setDepressionScreeningToDate(defaultState.toDate);
        setDepressionScreeningLoadedTeam(defaultState.loadedTeam);
        setDepressionScreeningIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setDepressionScreeningShowInActiveTeam(defaultState.showInActiveTeams);

        // Diabetic Eye Exam
        setDiabeticEyeExamHealthPlans(defaultState.healthPlans);
        setDiabeticEyeExamTeam(defaultState.team);
        setDiabeticEyeExamDateFilter(defaultState.filter);
        setDiabeticEyeExamFromDate(defaultState.fromDate);
        setDiabeticEyeExamToDate(defaultState.toDate);
        setDiabeticEyeExamLoadedTeam(defaultState.loadedTeam);
        setDiabeticEyeExamIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setDiabeticEyeExamShowInActiveTeam(defaultState.showInActiveTeams);

        // HbA1c
        setHgba1cHealthPlans(defaultState.healthPlans);
        setHgba1cPopulation(defaultState.population);
        setHgba1cTeam(defaultState.team);
        setHgba1cDateFilter(defaultState.filter);
        setHgba1cFromDate(defaultState.fromDate);
        setHgba1cToDate(defaultState.toDate);
        setHgba1cLoadedTeam(defaultState.loadedTeam);
        setHgba1cIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setHgba1cShowInActiveTeam(defaultState.showInActiveTeams);

        // HbA1c for BH Dx
        setHgbA1cForBhDxHealthPlans(defaultState.healthPlans);
        setHgbA1cForBhDxTeam(defaultState.team);
        setHgbA1cForBhDxDateFilter(defaultState.filter);
        setHgbA1cForBhDxFromDate(defaultState.fromDate);
        setHgbA1cForBhDxToDate(defaultState.toDate);
        setHgbA1cForBhDxLoadedTeam(defaultState.loadedTeam);
        setHgbA1cForBhDxIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setHgbA1cForBhDxShowInActiveTeam(defaultState.showInActiveTeams);

        // Lipid for BH Dx
        setLipidForBhDxHealthPlans(defaultState.healthPlans);
        setLipidForBhDxTeam(defaultState.team);
        setLipidForBhDxDateFilter(defaultState.filter);
        setLipidForBhDxFromDate(defaultState.fromDate);
        setLipidForBhDxToDate(defaultState.toDate);
        setLipidForBhDxLoadedTeam(defaultState.loadedTeam);
        setLipidForBhDxIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setLipidForBhDxShowInActiveTeam(defaultState.showInActiveTeams);

        // Medication Reconciliation
        setMedicationReconciliationHealthPlans(defaultState.healthPlans);
        setMedicationReconciliationTeam(defaultState.team);
        setMedicationReconciliationDateFilter(defaultState.filter);
        setMedicationReconciliationFromDate(defaultState.fromDate);
        setMedicationReconciliationToDate(defaultState.toDate);
        setMedicationReconciliationLoadedTeam(defaultState.loadedTeam);
        setMedicationReconciliationIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setMedicationReconciliationShowInActiveTeam(defaultState.showInActiveTeams);

        // Cervical Cancer Screening
        setCervicalCancerScreeningHealthPlans(defaultState.healthPlans);
        setCervicalCancerScreeningTeam(defaultState.team);
        setCervicalCancerScreeningDateFilter(defaultState.filter);
        setCervicalCancerScreeningFromDate(defaultState.fromDate);
        setCervicalCancerScreeningToDate(defaultState.toDate);
        setCervicalCancerScreeningLoadedTeam(defaultState.loadedTeam);
        setCervicalCancerScreeningIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setCervicalCancerScreeningShowInActiveTeam(defaultState.showInActiveTeams);

        // Colorectal Cancer Screening
        setColorectalCancerScreeningHealthPlans(defaultState.healthPlans);
        setColorectalCancerScreeningTeam(defaultState.team);
        setColorectalCancerScreeningDateFilter(defaultState.filter);
        setColorectalCancerScreeningFromDate(defaultState.fromDate);
        setColorectalCancerScreeningToDate(defaultState.toDate);
        setColorectalCancerScreeningLoadedTeam(defaultState.loadedTeam);
        setColorectalCancerScreeningIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setColorectalCancerScreeningShowInActiveTeam(defaultState.showInActiveTeams);

        // BH Diagnostic
        setBhDiagnosticHealthPlans(defaultState.healthPlans);
        setBhDiagnosticTeam(defaultState.team);
        setBhDiagnosticDateFilter(defaultState.filter);
        setBhDiagnosticFromDate(defaultState.fromDate);
        setBhDiagnosticToDate(defaultState.toDate);
        setBhDiagnosticLoadedTeam(defaultState.loadedTeam);
        setBhDiagnosticIncludePastOpenStatus(defaultState.includePastOpenStatus);
        setBhDiagnosticShowInActiveTeam(defaultState.showInActiveTeams);
    };

    // Save to localStorage on change
    useEffect(() => {
        const data = {
            selectedTeam,
            showInActiveTeams,
            hedisType,
            healthPlans,
            population,
            team,
            filter,
            fDate,
            tDate,
            loadedTeam,
            includePastOpenStatus,
            showInActiveTeam,
            hasElevatedFilterAccess,

            adultHealthHealthPlans,
            adultHealthTeam,
            adultHealthDateFilter,
            adultHealthFromDate,
            adultHealthToDate,
            adultHealthLoadedTeam,
            adultHealthIncludePastOpenStatus,
            adultHealthShowInActiveTeam,

            bmiHealthPlans,
            bmiTeam,
            bmiDateFilter,
            bmiFromDate,
            bmiToDate,
            bmiLoadedTeam,
            bmiIncludePastOpenStatus,
            bmiShowInActiveTeam,

            bpHealthPlans,
            bpPopulation,
            bpTeam,
            bpDateFilter,
            bpFromDate,
            bpToDate,
            bpLoadedTeam,
            bpIncludePastOpenStatus,
            bpShowInActiveTeam,

            breastCancerScreeningHealthPlans,
            breastCancerScreeningTeam,
            breastCancerScreeningDateFilter,
            breastCancerScreeningFromDate,
            breastCancerScreeningToDate,
            breastCancerScreeningLoadedTeam,
            breastCancerScreeningIncludePastOpenStatus,
            breastCancerScreeningShowInActiveTeam,

            depressionFollowUpHealthPlans,
            depressionFollowUpTeam,
            depressionFollowUpDateFilter,
            depressionFollowUpFromDate,
            depressionFollowUpToDate,
            depressionFollowUpLoadedTeam,
            depressionFollowUpIncludePastOpenStatus,
            depressionFollowUpShowInActiveTeam,

            depressionScreeningHealthPlans,
            depressionScreeningTeam,
            depressionScreeningDateFilter,
            depressionScreeningFromDate,
            depressionScreeningToDate,
            depressionScreeningLoadedTeam,
            depressionScreeningIncludePastOpenStatus,
            depressionScreeningShowInActiveTeam,

            diabeticEyeExamHealthPlans,
            diabeticEyeExamTeam,
            diabeticEyeExamDateFilter,
            diabeticEyeExamFromDate,
            diabeticEyeExamToDate,
            diabeticEyeExamLoadedTeam,
            diabeticEyeExamIncludePastOpenStatus,
            diabeticEyeExamShowInActiveTeam,

            hgba1cHealthPlans,
            hgba1cPopulation,
            hgba1cTeam,
            hgba1cDateFilter,
            hgba1cFromDate,
            hgba1cToDate,
            hgba1cLoadedTeam,
            hgba1cIncludePastOpenStatus,
            hgba1cShowInActiveTeam,

            hgbA1cForBhDxHealthPlans,
            hgbA1cForBhDxTeam,
            hgbA1cForBhDxDateFilter,
            hgbA1cForBhDxFromDate,
            hgbA1cForBhDxToDate,
            hgbA1cForBhDxLoadedTeam,
            hgbA1cForBhDxIncludePastOpenStatus,
            hgbA1cForBhDxShowInActiveTeam,

            lipidForBhDxHealthPlans,
            lipidForBhDxTeam,
            lipidForBhDxDateFilter,
            lipidForBhDxFromDate,
            lipidForBhDxToDate,
            lipidForBhDxLoadedTeam,
            lipidForBhDxIncludePastOpenStatus,
            lipidForBhDxShowInActiveTeam,

            medicationReconciliationHealthPlans,
            medicationReconciliationTeam,
            medicationReconciliationDateFilter,
            medicationReconciliationFromDate,
            medicationReconciliationToDate,
            medicationReconciliationLoadedTeam,
            medicationReconciliationIncludePastOpenStatus,
            medicationReconciliationShowInActiveTeam,

            cervicalCancerScreeningHealthPlans,
            cervicalCancerScreeningTeam,
            cervicalCancerScreeningDateFilter,
            cervicalCancerScreeningFromDate,
            cervicalCancerScreeningToDate,
            cervicalCancerScreeningLoadedTeam,
            cervicalCancerScreeningIncludePastOpenStatus,
            cervicalCancerScreeningShowInActiveTeam,

            colorectalCancerScreeningHealthPlans,
            colorectalCancerScreeningTeam,
            colorectalCancerScreeningDateFilter,
            colorectalCancerScreeningFromDate,
            colorectalCancerScreeningToDate,
            colorectalCancerScreeningLoadedTeam,
            colorectalCancerScreeningIncludePastOpenStatus,
            colorectalCancerScreeningShowInActiveTeam,

            bhDiagnosticHealthPlans,
            bhDiagnosticTeam,
            bhDiagnosticDateFilter,
            bhDiagnosticFromDate,
            bhDiagnosticToDate,
            bhDiagnosticLoadedTeam,
            bhDiagnosticIncludePastOpenStatus,
            bhDiagnosticShowInActiveTeam,
        };

        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
    }, [
        selectedTeam,
        showInActiveTeams,
        hedisType,
        healthPlans,
        population,
        team,
        filter,
        fDate,
        tDate,
        loadedTeam,
        includePastOpenStatus,
        showInActiveTeam,
        hasElevatedFilterAccess,
        adultHealthHealthPlans,
        adultHealthTeam,
        adultHealthDateFilter,
        adultHealthFromDate,
        adultHealthToDate,
        adultHealthLoadedTeam,
        adultHealthIncludePastOpenStatus,
        adultHealthShowInActiveTeam,
        bmiHealthPlans,
        bmiTeam,
        bmiDateFilter,
        bmiFromDate,
        bmiToDate,
        bmiLoadedTeam,
        bmiIncludePastOpenStatus,
        bmiShowInActiveTeam,
        bpHealthPlans,
        bpPopulation,
        bpTeam,
        bpDateFilter,
        bpFromDate,
        bpToDate,
        bpLoadedTeam,
        bpIncludePastOpenStatus,
        bpShowInActiveTeam,
        breastCancerScreeningHealthPlans,
        breastCancerScreeningTeam,
        breastCancerScreeningDateFilter,
        breastCancerScreeningFromDate,
        breastCancerScreeningToDate,
        breastCancerScreeningLoadedTeam,
        breastCancerScreeningIncludePastOpenStatus,
        breastCancerScreeningShowInActiveTeam,
        depressionFollowUpHealthPlans,
        depressionFollowUpTeam,
        depressionFollowUpDateFilter,
        depressionFollowUpFromDate,
        depressionFollowUpToDate,
        depressionFollowUpLoadedTeam,
        depressionFollowUpIncludePastOpenStatus,
        depressionFollowUpShowInActiveTeam,
        depressionScreeningHealthPlans,
        depressionScreeningTeam,
        depressionScreeningDateFilter,
        depressionScreeningFromDate,
        depressionScreeningToDate,
        depressionScreeningLoadedTeam,
        depressionScreeningIncludePastOpenStatus,
        depressionScreeningShowInActiveTeam,
        diabeticEyeExamHealthPlans,
        diabeticEyeExamTeam,
        diabeticEyeExamDateFilter,
        diabeticEyeExamFromDate,
        diabeticEyeExamToDate,
        diabeticEyeExamLoadedTeam,
        diabeticEyeExamIncludePastOpenStatus,
        diabeticEyeExamShowInActiveTeam,
        hgba1cHealthPlans,
        hgba1cPopulation,
        hgba1cTeam,
        hgba1cDateFilter,
        hgba1cFromDate,
        hgba1cToDate,
        hgba1cLoadedTeam,
        hgba1cIncludePastOpenStatus,
        hgba1cShowInActiveTeam,
        hgbA1cForBhDxHealthPlans,
        hgbA1cForBhDxTeam,
        hgbA1cForBhDxDateFilter,
        hgbA1cForBhDxFromDate,
        hgbA1cForBhDxToDate,
        hgbA1cForBhDxLoadedTeam,
        hgbA1cForBhDxIncludePastOpenStatus,
        hgbA1cForBhDxShowInActiveTeam,
        lipidForBhDxHealthPlans,
        lipidForBhDxTeam,
        lipidForBhDxDateFilter,
        lipidForBhDxFromDate,
        lipidForBhDxToDate,
        lipidForBhDxLoadedTeam,
        lipidForBhDxIncludePastOpenStatus,
        lipidForBhDxShowInActiveTeam,
        medicationReconciliationHealthPlans,
        medicationReconciliationTeam,
        medicationReconciliationDateFilter,
        medicationReconciliationFromDate,
        medicationReconciliationToDate,
        medicationReconciliationLoadedTeam,
        medicationReconciliationIncludePastOpenStatus,
        medicationReconciliationShowInActiveTeam,
        cervicalCancerScreeningHealthPlans,
        cervicalCancerScreeningTeam,
        cervicalCancerScreeningDateFilter,
        cervicalCancerScreeningFromDate,
        cervicalCancerScreeningToDate,
        cervicalCancerScreeningLoadedTeam,
        cervicalCancerScreeningIncludePastOpenStatus,
        cervicalCancerScreeningShowInActiveTeam,
        colorectalCancerScreeningHealthPlans,
        colorectalCancerScreeningTeam,
        colorectalCancerScreeningDateFilter,
        colorectalCancerScreeningFromDate,
        colorectalCancerScreeningToDate,
        colorectalCancerScreeningLoadedTeam,
        colorectalCancerScreeningIncludePastOpenStatus,
        colorectalCancerScreeningShowInActiveTeam,
        bhDiagnosticHealthPlans,
        bhDiagnosticTeam,
        bhDiagnosticDateFilter,
        bhDiagnosticFromDate,
        bhDiagnosticToDate,
        bhDiagnosticLoadedTeam,
        bhDiagnosticIncludePastOpenStatus,
        bhDiagnosticShowInActiveTeam,
    ]);

    const value = {
        selectedTeam,
        setSelectedTeam,
        showInActiveTeams,
        setShowInActiveTeams,

        hedisType,
        setHedisType,
        healthPlans,
        setHealthPlans,
        population,
        setPopulation,
        team,
        setTeam,
        filter,
        setFilter,
        fDate,
        setFDate,
        tDate,
        setTDate,
        loadedTeam,
        setLoadedTeam,
        includePastOpenStatus,
        setIncludePastOpenStatus,
        showInActiveTeam,
        setShowInActiveTeam,
        hasElevatedFilterAccess,
        setHasElevatedFilterAccess,

        adultHealthHealthPlans,
        setAdultHealthHealthPlans,
        adultHealthTeam,
        setAdultHealthTeam,
        adultHealthDateFilter,
        setAdultHealthDateFilter,
        adultHealthFromDate,
        setAdultHealthFromDate,
        adultHealthToDate,
        setAdultHealthToDate,
        adultHealthLoadedTeam,
        setAdultHealthLoadedTeam,
        adultHealthIncludePastOpenStatus,
        setAdultHealthIncludePastOpenStatus,
        adultHealthShowInActiveTeam,
        setAdultHealthShowInActiveTeam,

        bmiHealthPlans,
        setBmiHealthPlans,
        bmiTeam,
        setBmiTeam,
        bmiDateFilter,
        setBmiDateFilter,
        bmiFromDate,
        setBmiFromDate,
        bmiToDate,
        setBmiToDate,
        bmiLoadedTeam,
        setBmiLoadedTeam,
        bmiIncludePastOpenStatus,
        setBmiIncludePastOpenStatus,
        bmiShowInActiveTeam,
        setBmiShowInActiveTeam,

        bpHealthPlans,
        setBpHealthPlans,
        bpPopulation,
        setBpPopulation,
        bpTeam,
        setBpTeam,
        bpDateFilter,
        setBpDateFilter,
        bpFromDate,
        setBpFromDate,
        bpToDate,
        setBpToDate,
        bpLoadedTeam,
        setBpLoadedTeam,
        bpIncludePastOpenStatus,
        setBpIncludePastOpenStatus,
        bpShowInActiveTeam,
        setBpShowInActiveTeam,

        breastCancerScreeningHealthPlans,
        setBreastCancerScreeningHealthPlans,
        breastCancerScreeningTeam,
        setBreastCancerScreeningTeam,
        breastCancerScreeningDateFilter,
        setBreastCancerScreeningDateFilter,
        breastCancerScreeningFromDate,
        setBreastCancerScreeningFromDate,
        breastCancerScreeningToDate,
        setBreastCancerScreeningToDate,
        breastCancerScreeningLoadedTeam,
        setBreastCancerScreeningLoadedTeam,
        breastCancerScreeningIncludePastOpenStatus,
        setBreastCancerScreeningIncludePastOpenStatus,
        breastCancerScreeningShowInActiveTeam,
        setBreastCancerScreeningShowInActiveTeam,

        depressionFollowUpHealthPlans,
        setDepressionFollowUpHealthPlans,
        depressionFollowUpTeam,
        setDepressionFollowUpTeam,
        depressionFollowUpDateFilter,
        setDepressionFollowUpDateFilter,
        depressionFollowUpFromDate,
        setDepressionFollowUpFromDate,
        depressionFollowUpToDate,
        setDepressionFollowUpToDate,
        depressionFollowUpLoadedTeam,
        setDepressionFollowUpLoadedTeam,
        depressionFollowUpIncludePastOpenStatus,
        setDepressionFollowUpIncludePastOpenStatus,
        depressionFollowUpShowInActiveTeam,
        setDepressionFollowUpShowInActiveTeam,

        depressionScreeningHealthPlans,
        setDepressionScreeningHealthPlans,
        depressionScreeningTeam,
        setDepressionScreeningTeam,
        depressionScreeningDateFilter,
        setDepressionScreeningDateFilter,
        depressionScreeningFromDate,
        setDepressionScreeningFromDate,
        depressionScreeningToDate,
        setDepressionScreeningToDate,
        depressionScreeningLoadedTeam,
        setDepressionScreeningLoadedTeam,
        depressionScreeningIncludePastOpenStatus,
        setDepressionScreeningIncludePastOpenStatus,
        depressionScreeningShowInActiveTeam,
        setDepressionScreeningShowInActiveTeam,

        diabeticEyeExamHealthPlans,
        setDiabeticEyeExamHealthPlans,
        diabeticEyeExamTeam,
        setDiabeticEyeExamTeam,
        diabeticEyeExamDateFilter,
        setDiabeticEyeExamDateFilter,
        diabeticEyeExamFromDate,
        setDiabeticEyeExamFromDate,
        diabeticEyeExamToDate,
        setDiabeticEyeExamToDate,
        diabeticEyeExamLoadedTeam,
        setDiabeticEyeExamLoadedTeam,
        diabeticEyeExamIncludePastOpenStatus,
        setDiabeticEyeExamIncludePastOpenStatus,
        diabeticEyeExamShowInActiveTeam,
        setDiabeticEyeExamShowInActiveTeam,

        hgba1cHealthPlans,
        setHgba1cHealthPlans,
        hgba1cPopulation,
        setHgba1cPopulation,
        hgba1cTeam,
        setHgba1cTeam,
        hgba1cDateFilter,
        setHgba1cDateFilter,
        hgba1cFromDate,
        setHgba1cFromDate,
        hgba1cToDate,
        setHgba1cToDate,
        hgba1cLoadedTeam,
        setHgba1cLoadedTeam,
        hgba1cIncludePastOpenStatus,
        setHgba1cIncludePastOpenStatus,
        hgba1cShowInActiveTeam,
        setHgba1cShowInActiveTeam,

        hgbA1cForBhDxHealthPlans,
        setHgbA1cForBhDxHealthPlans,
        hgbA1cForBhDxTeam,
        setHgbA1cForBhDxTeam,
        hgbA1cForBhDxDateFilter,
        setHgbA1cForBhDxDateFilter,
        hgbA1cForBhDxFromDate,
        setHgbA1cForBhDxFromDate,
        hgbA1cForBhDxToDate,
        setHgbA1cForBhDxToDate,
        hgbA1cForBhDxLoadedTeam,
        setHgbA1cForBhDxLoadedTeam,
        hgbA1cForBhDxIncludePastOpenStatus,
        setHgbA1cForBhDxIncludePastOpenStatus,
        hgbA1cForBhDxShowInActiveTeam,
        setHgbA1cForBhDxShowInActiveTeam,

        lipidForBhDxHealthPlans,
        setLipidForBhDxHealthPlans,
        lipidForBhDxTeam,
        setLipidForBhDxTeam,
        lipidForBhDxDateFilter,
        setLipidForBhDxDateFilter,
        lipidForBhDxFromDate,
        setLipidForBhDxFromDate,
        lipidForBhDxToDate,
        setLipidForBhDxToDate,
        lipidForBhDxLoadedTeam,
        setLipidForBhDxLoadedTeam,
        lipidForBhDxIncludePastOpenStatus,
        setLipidForBhDxIncludePastOpenStatus,
        lipidForBhDxShowInActiveTeam,
        setLipidForBhDxShowInActiveTeam,

        medicationReconciliationHealthPlans,
        setMedicationReconciliationHealthPlans,
        medicationReconciliationTeam,
        setMedicationReconciliationTeam,
        medicationReconciliationDateFilter,
        setMedicationReconciliationDateFilter,
        medicationReconciliationFromDate,
        setMedicationReconciliationFromDate,
        medicationReconciliationToDate,
        setMedicationReconciliationToDate,
        medicationReconciliationLoadedTeam,
        setMedicationReconciliationLoadedTeam,
        medicationReconciliationIncludePastOpenStatus,
        setMedicationReconciliationIncludePastOpenStatus,
        medicationReconciliationShowInActiveTeam,
        setMedicationReconciliationShowInActiveTeam,

        cervicalCancerScreeningHealthPlans,
        setCervicalCancerScreeningHealthPlans,
        cervicalCancerScreeningTeam,
        setCervicalCancerScreeningTeam,
        cervicalCancerScreeningDateFilter,
        setCervicalCancerScreeningDateFilter,
        cervicalCancerScreeningFromDate,
        setCervicalCancerScreeningFromDate,
        cervicalCancerScreeningToDate,
        setCervicalCancerScreeningToDate,
        cervicalCancerScreeningLoadedTeam,
        setCervicalCancerScreeningLoadedTeam,
        cervicalCancerScreeningIncludePastOpenStatus,
        setCervicalCancerScreeningIncludePastOpenStatus,
        cervicalCancerScreeningShowInActiveTeam,
        setCervicalCancerScreeningShowInActiveTeam,

        colorectalCancerScreeningHealthPlans,
        setColorectalCancerScreeningHealthPlans,
        colorectalCancerScreeningTeam,
        setColorectalCancerScreeningTeam,
        colorectalCancerScreeningDateFilter,
        setColorectalCancerScreeningDateFilter,
        colorectalCancerScreeningFromDate,
        setColorectalCancerScreeningFromDate,
        colorectalCancerScreeningToDate,
        setColorectalCancerScreeningToDate,
        colorectalCancerScreeningLoadedTeam,
        setColorectalCancerScreeningLoadedTeam,
        colorectalCancerScreeningIncludePastOpenStatus,
        setColorectalCancerScreeningIncludePastOpenStatus,
        colorectalCancerScreeningShowInActiveTeam,
        setColorectalCancerScreeningShowInActiveTeam,

        bhDiagnosticHealthPlans,
        setBhDiagnosticHealthPlans,
        bhDiagnosticTeam,
        setBhDiagnosticTeam,
        bhDiagnosticDateFilter,
        setBhDiagnosticDateFilter,
        bhDiagnosticFromDate,
        setBhDiagnosticFromDate,
        bhDiagnosticToDate,
        setBhDiagnosticToDate,
        bhDiagnosticLoadedTeam,
        setBhDiagnosticLoadedTeam,
        bhDiagnosticIncludePastOpenStatus,
        setBhDiagnosticIncludePastOpenStatus,
        bhDiagnosticShowInActiveTeam,
        setBhDiagnosticShowInActiveTeam,

        resetHedisMetricContext,
    };

    return <HedisMetricsContext.Provider value={value}>{children}</HedisMetricsContext.Provider>;
};

export const useHedisMetricsContext = () => React.useContext(HedisMetricsContext);
